import { DOCUMENT } from '@angular/common';
import { computed, inject, Injectable, signal } from '@angular/core';
import { ApplicationStateFacade } from 'mbs-ui-shared@facades/application.facade';
import { UiStorageKey } from 'mbs-ui-shared@models';
import { take } from 'rxjs/operators';

export type MainMenuLayout = 'horizontal' | 'vertical';

@Injectable({ providedIn: 'root' })
export class NavLayoutService {
  private readonly document = inject<Document>(DOCUMENT);
  private readonly applicationStateFacade = inject(ApplicationStateFacade);

  readonly layout = signal<MainMenuLayout>('horizontal');
  readonly collapsed = signal<boolean>(false);
  readonly isVertical = computed(() => this.layout() === 'vertical');

  constructor() {
    this.apply(
      (localStorage.getItem(UiStorageKey.MainMenuLayout) as MainMenuLayout) || 'horizontal',
      localStorage.getItem(UiStorageKey.MainMenuCollapsed) === 'true'
    );
    this.restoreFromUiStorage();
  }

  setLayout(layout: MainMenuLayout): void {
    if (layout === this.layout()) return;

    this.apply(layout, this.collapsed());
    this.applicationStateFacade.updateStorageSetting(UiStorageKey.MainMenuLayout, layout).subscribe();
  }

  toggleCollapsed(): void {
    this.setCollapsed(!this.collapsed());
  }

  setCollapsed(collapsed: boolean): void {
    if (collapsed === this.collapsed()) return;

    this.apply(this.layout(), collapsed);
    this.applicationStateFacade.updateStorageSetting(UiStorageKey.MainMenuCollapsed, String(collapsed)).subscribe();
  }

  private restoreFromUiStorage(): void {
    this.applicationStateFacade
      .getUiStorage()
      .pipe(take(1))
      .subscribe((storage) => {
        const layout = storage?.[UiStorageKey.MainMenuLayout] as MainMenuLayout;
        const collapsed = storage?.[UiStorageKey.MainMenuCollapsed];

        this.apply(layout || this.layout(), collapsed === undefined ? this.collapsed() : collapsed === 'true');
      });
  }

  private apply(layout: MainMenuLayout, collapsed: boolean): void {
    this.layout.set(layout);
    this.collapsed.set(collapsed);

    const root = this.document.documentElement;

    root.setAttribute('data-mbs-nav', layout);
    collapsed ? root.setAttribute('data-mbs-nav-collapsed', '') : root.removeAttribute('data-mbs-nav-collapsed');

    localStorage.setItem(UiStorageKey.MainMenuLayout, layout);
    localStorage.setItem(UiStorageKey.MainMenuCollapsed, String(collapsed));
  }
}
